import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { Trash2 } from "lucide-react";
import { paths } from "../../app/paths.js";
import { markUsed } from "../../app/recentUse.js";
import { useBreachScan } from "../../data/breach.js";
import { useDeleteItem, useRestoreItem, useSaveEntry, useToggleFavorite, useVaultEntries } from "../../data/vault.js";
import { formatBreachCount } from "../../lib/breach.js";
import type { Entry } from "../../lib/entries.js";
import { Alert, Button, ConfirmInline } from "../../ui/primitives.js";
import { useToast } from "../../ui/Toast.js";
import type { VaultPayload } from "../../vaultPayload.js";
import { EntryEditor, EntryView, draftFrom, emptyDraft } from "./EntryParts.js";
import { typeFromParam } from "./itemTypes.js";

export type InspectorMode = "view" | "edit" | "new";

export interface InspectorProps {
  readonly mode: InspectorMode;
  readonly entry: Entry | undefined;
  readonly missing: boolean;
  readonly prefill: string | undefined;
  readonly folders: ReadonlyArray<string>;
  readonly listSearch: string;
}

export function ItemInspector(props: InspectorProps): ReactNode {
  if (props.mode === "new" || props.mode === "edit") {
    return <PersonalEditor {...props} />;
  }
  if (props.missing || props.entry === undefined) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-10 text-center">
        <p className="text-base font-semibold text-foam">Eintrag nicht gefunden</p>
        <p className="text-sm text-mist">Er wurde gelöscht oder liegt nicht in diesem Tresor.</p>
      </div>
    );
  }
  return <PersonalView entry={props.entry} listSearch={props.listSearch} />;
}

function PersonalView(props: { readonly entry: Entry; readonly listSearch: string }): ReactNode {
  const { entry } = props;
  const navigate = useNavigate();
  const toast = useToast();
  const toggleFavorite = useToggleFavorite();
  const remove = useDeleteItem();
  const restore = useRestoreItem();
  const [confirming, setConfirming] = useState(false);
  const vault = useVaultEntries();
  const all = useMemo((): ReadonlyArray<Entry> => vault.data?.entries ?? [], [vault.data]);
  const breachCount = useBreachScan(all).counts.get(entry.meta.id);

  function deleteEntry(): void {
    remove.mutate(entry.meta.id, {
      onSuccess: (): void => {
        void navigate(`${paths.vault}${props.listSearch}`);
        toast.notify("In den Papierkorb verschoben.", {
          action: {
            label: "Rückgängig",
            run: (): void => {
              restore.mutate(entry.meta.id, {
                onSuccess: (): void => toast.notify("Eintrag wiederhergestellt.", { tone: "success" }),
                onError: (): void => toast.notify("Wiederherstellen fehlgeschlagen.", { tone: "danger" })
              });
            }
          }
        });
      },
      onError: (): void => {
        toast.notify("Löschen fehlgeschlagen. Bitte erneut versuchen.", { tone: "danger" });
        setConfirming(false);
      }
    });
  }

  const footer = confirming ? (
    <ConfirmInline
      message="Eintrag in den Papierkorb verschieben?"
      confirmLabel="Verschieben"
      pending={remove.isPending}
      onConfirm={deleteEntry}
      onCancel={(): void => setConfirming(false)}
    />
  ) : (
    <Button variant="ghost" size="sm" icon={Trash2} className="self-start text-buoy hover:bg-buoy/10 hover:text-buoy" onClick={(): void => setConfirming(true)}>
      In den Papierkorb
    </Button>
  );

  return (
    <EntryView
      key={entry.meta.id}
      data={entry.data}
      folder={entry.meta.folder}
      favorite={entry.meta.favorite}
      version={entry.meta.version}
      updatedAt={entry.meta.updatedAt}
      onEdit={(): void => void navigate(`${paths.vaultEdit(entry.meta.id)}${props.listSearch}`)}
      onToggleFavorite={(): void => toggleFavorite.mutate(entry)}
      favoritePending={toggleFavorite.isPending}
      onUsed={(): void => markUsed(entry.meta.id)}
      notice={
        breachCount !== undefined ? (
          <Alert tone="danger">
            <strong className="font-semibold">In Datenlecks gefunden.</strong> Dieses Passwort taucht {formatBreachCount(breachCount)} in
            öffentlichen Datenlecks auf. Ändere es beim Dienst und hier im Eintrag.
          </Alert>
        ) : undefined
      }
      footer={footer}
    />
  );
}

function PersonalEditor(props: InspectorProps): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const save = useSaveEntry();
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  const entry = props.entry;
  const isNew = props.mode === "new";
  const initial = entry !== undefined && !isNew
    ? draftFrom(entry.data, entry.meta.folder, entry.meta.favorite)
    : emptyDraft(props.prefill, typeFromParam(new URLSearchParams(props.listSearch).get("type")));
  const back = isNew ? `${paths.vault}${props.listSearch}` : `${paths.vaultItem(entry?.meta.id ?? "")}${props.listSearch}`;

  function onSubmit(payload: VaultPayload, draft: { readonly folder: string; readonly favorite: boolean }): void {
    setServerError(undefined);
    save.mutate(
      {
        id: isNew ? undefined : entry?.meta.id,
        version: isNew ? undefined : entry?.meta.version,
        folder: draft.folder,
        favorite: draft.favorite,
        payload
      },
      {
        onSuccess: (saved: Entry): void => {
          toast.notify(isNew ? "Eintrag angelegt." : "Änderungen gespeichert.", { tone: "success" });
          void navigate(`${paths.vaultItem(saved.meta.id)}${props.listSearch}`, { replace: true });
        },
        onError: (err: Error): void => {
          setServerError(
            err.message.includes("Version") || err.message.includes("Mismatch") || err.message.includes("current")
              ? "Der Eintrag wurde zwischenzeitlich geändert. Lade die Seite neu und versuche es erneut."
              : "Speichern fehlgeschlagen. Bitte erneut versuchen."
          );
        }
      }
    );
  }

  return (
    <EntryEditor
      // A fresh key per navigation: "new" right after a save must not inherit the previous draft.
      key={isNew ? `new-${location.key}` : entry?.meta.id ?? "missing"}
      mode={props.mode === "new" ? "new" : "edit"}
      initial={initial}
      folders={props.folders}
      saving={save.isPending}
      serverError={serverError}
      onSubmit={onSubmit}
      onCancel={(): void => void navigate(back)}
    />
  );
}
